import type { SignSecret } from '../types/sign'
import { createTraceID } from './traceId'
import { SHA256Algo } from 'crypto-es/lib/sha256.js'

interface SignConfig {
  headers: Record<string, string>
  params?: Record<string, any>
  data?: any
}

// 签名请求通用配置
const HEADER_CONFIG = {
  ['Xh-Sdk-Version']: '3.5.0',
  ['Xh-Os']: 'web',
  ['Sign-Version']: '3.0.1',
} as const

const REQUIRED_HEADER_FIELDS = ['Xh-Os', 'Xh-Sdk-Version', 'Sign-Version', 'Key', 'Request-Id'] as const

/**
 * 设置请求签名
 * @param config axios配置对象
 * @param secret 密钥信息
 * @returns 添加签名后的配置对象
 */
export async function setRequestSign(config: SignConfig, secret: SignSecret): Promise<SignConfig> {
  const { key, publicKey } = secret

  const headerConfig = {
    ...HEADER_CONFIG,
    ['Key']: key,
    ['Request-Id']: createTraceID(),
  }

  config.headers = { ...config.headers, ...headerConfig }

  const { sign } = await signV3(config, publicKey)
  config.headers['Sign'] = sign

  return config
}

/**
 * 生成V3版本的签名
 * @param config 请求配置
 * @param publicKey 公钥
 * @returns 签名结果
 */
export async function signV3(config: SignConfig, publicKey: string): Promise<{ sign: string }> {
  const headers = config.headers

  // 检查必要头部字段
  const missingField = REQUIRED_HEADER_FIELDS.find((field) => !headers[field])
  if (missingField) {
    throw new Error(`Missing required header field: ${missingField}`)
  }

  // 构建签名字符串
  const headerSumStr = REQUIRED_HEADER_FIELDS.reduce((pre, cur) => pre + (headers[cur] || ''), '')
  const queryStringStr = new URLSearchParams(config.params || {}).toString()
  const bodyStr = JSON.stringify(config.data || '')

  // 计算SHA256哈希
  const sha256Hash = SHA256Algo.create()
  sha256Hash.update(headerSumStr)
  sha256Hash.update(queryStringStr)
  sha256Hash.update(bodyStr)

  const reqHash = sha256Hash.finalize().words

  // 转换为ArrayBuffer
  const buffer = new ArrayBuffer(reqHash.length * 4)
  const dataView = new DataView(buffer)
  for (let i = 0; i < reqHash.length; i++) {
    dataView.setInt32(i * 4, reqHash[i]!, false)
  }

  // 导入公钥并加密
  const cryptoKey = await importPublicKey(publicKey)
  if (!cryptoKey) {
    throw new Error('Failed to import public key')
  }

  const encrypted = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, cryptoKey, dataView)

  const sign = btoa(String.fromCharCode(...new Uint8Array(encrypted)))
  return { sign }
}

/**
 * 导入公钥
 * @param publicKey PEM格式的公钥
 * @returns 导入的CryptoKey对象
 */
async function importPublicKey(publicKey: string): Promise<CryptoKey> {
  const pemMatch = publicKey.match(/-----BEGIN PUBLIC KEY-----\s*([\s\S]*?)\s*-----END PUBLIC KEY-----/)
  if (!pemMatch) {
    throw new Error('Invalid PEM format')
  }

  const pemContents = pemMatch[1]!.replace(/\s/g, '')
  const binaryDerString = window.atob(pemContents)
  const binaryDer = str2ab(binaryDerString)

  return window.crypto.subtle.importKey(
    'spki',
    binaryDer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-1',
    },
    true,
    ['encrypt']
  )
}

/**
 * 将字符串转换为ArrayBuffer
 * @param str 输入字符串
 * @returns ArrayBuffer对象
 */
function str2ab(str: string): ArrayBuffer {
  const buf = new ArrayBuffer(str.length)
  const bufView = new Uint8Array(buf)
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i)
  }
  return buf
}
